import { Box, Text, Skeleton, Flex, Icon } from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList, ReferenceLine } from 'recharts';
import { DomainStatItem, NEGATIVE_METRICS, ReportingComparison } from '../../../types/reporting';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import { ChartMetricType, formatChartValue, formatTooltipValue, getMetricFormat, getMetricLabel } from './ChartMetricSelector';
import { getBarChartYDomain, getBarChartYTicks } from '../../../utils/chartUtils';
import { PRIMARY_CHART_COLOR } from '../shared/formatters';
import { DOMAIN_RANKING_AXIS_BAND_MAX, getRankingXAxisLayout, RANKING_AXIS_TICK_MARGIN, truncateRankingAxisName } from '../shared/dateUtils';
import { MARKETPLACE_CHART_TOP_MARGIN, MarketplaceChartLegend } from '../shared/MarketplaceChartLegend';
import {
    chartValueFontSize,
    comparisonBarLabelListContent,
    verticalBarTopLabelListProps,
    yAxisTickFontSize,
    yAxisWidth,
} from '../shared/barValueLabels';
import { TbTrendingUp, TbTrendingDown } from 'react-icons/tb';
import { getSourceColor, getSourceLabel } from '../../../components/SourceBadge';

const SOURCE_SERIES_PREFIX = 'src__';

interface VerticalRankingChartProps {
    data: DomainStatItem[];
    isLoading: boolean;
    metric: ChartMetricType;
    domainLabel: string;
    maxItems?: number;
    height?: number | string;
    comparison?: ReportingComparison;
    preset?: string;
    showPriorBars?: boolean;
    isVendor?: boolean;
    isLocation?: boolean;
    isInsurance?: boolean;
    isOEM?: boolean;
    /** Cluster each entity's bar by marketplace (uses item.bySource) */
    marketplaceBreakdown?: boolean;
}

const CustomTooltip = ({ active, payload, label, metric, metricLabel, format, preset }: any) => {
    if (active && payload && payload.length) {
        const item = payload[0].payload;
        const value = (item as any)[metric] ?? 0;
        const comparisonLabel = getSummaryComparisonLabel(preset) ?? 'vs prior';
        const formatter = (val: number) => formatTooltipValue(val, format);

        const priorValue = typeof item?.priorMetricValue === 'number' ? item.priorMetricValue : item?.prior?.[metric];

        let change: number | undefined;
        let changeLabel = '';

        if (item.change && item.change[metric] !== undefined) {
            change = item.change[metric];
            changeLabel = format === 'percent' ? 'pp' : '%';
        }

        const isPositiveChange = change !== undefined && change >= 0;
        const IconComponent = isPositiveChange ? TbTrendingUp : TbTrendingDown;

        const isNegativeMetric = NEGATIVE_METRICS.includes(metric as any);
        let colorString = "gray.500";
        if (change !== undefined) {
            colorString = isNegativeMetric
                ? (isPositiveChange ? "red.500" : "green.500")
                : (isPositiveChange ? "green.500" : "red.500");
        }

        return (
            <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="150px">
                <Text fontSize="xs" color="gray.500" mb={1}>{item?.fullName || label}</Text>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700">{metricLabel}</Text>
                <Text fontSize="lg" fontWeight="bold" color="#0071E3" mb={1}>{formatter(value)}</Text>
                {priorValue !== undefined && priorValue !== null && (
                    <Text fontSize="xs" color="gray.500" mb={1}>Prior: {formatter(priorValue)}</Text>
                )}
                {change !== undefined && (
                    <Flex align="center" gap={1}>
                        <Icon as={IconComponent as React.ElementType} color={colorString} boxSize={3} />
                        <Text fontSize="xs" fontWeight="medium" color={colorString}>
                            {Math.abs(change).toFixed(1)}{changeLabel}
                            <Text as="span" color="gray.400" ml={1} fontWeight="normal">{comparisonLabel}</Text>
                        </Text>
                    </Flex>
                )}
                {change === undefined && priorValue !== undefined && priorValue !== null && (
                    <Text fontSize="xs" color="gray.400">—</Text>
                )}
            </Box>
        );
    }
    return null;
};

// Tooltip used in marketplace-breakdown mode: lists each marketplace's value
// for the hovered entity plus an overall total.
export const MarketplaceTooltip = ({ active, payload, metricLabel, format, sourceKeys, metric, hasPrior, preset }: any) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const formatter = (val: number) => formatTooltipValue(val, format);
    const isPercent = format === 'percent';
    const comparisonLabel = getSummaryComparisonLabel(preset) ?? 'vs prior';

    const entries = sourceKeys
        .map((src: string, i: number) => ({
            src,
            label: getSourceLabel(src),
            color: getSourceColor(src, i),
            value: Number(row[`${SOURCE_SERIES_PREFIX}${src}`]) || 0,
            priorValue: hasPrior && typeof row[`${SOURCE_SERIES_PREFIX}${src}__prior`] === 'number'
                ? row[`${SOURCE_SERIES_PREFIX}${src}__prior`] as number
                : undefined,
        }))
        .filter((e: any) => e.value !== 0 || e.priorValue !== undefined);

    // Prefer parent metric (distinct orders / true entity total). Summing bySource
    // overcounts when an RO touches multiple marketplaces; percent sums are meaningless.
    const total = typeof row[metric] === 'number'
        ? Number(row[metric])
        : entries.reduce((s: number, e: any) => s + e.value, 0);
    const totalLabel = metric === 'orderCount' ? 'Distinct Orders' : 'Total';
    const totalChange = typeof row.change?.[metric] === 'number' ? row.change[metric] as number : undefined;
    const changeLabel = isPercent ? 'pp' : '%';

    return (
        <Box bg="white" p={3} border="1px solid" borderColor="gray.200" borderRadius="lg" boxShadow="none" minW="200px">
            <Text fontSize="xs" color="gray.500" mb={1}>{row.fullName || row.displayName}</Text>
            <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={1.5}>{metricLabel}</Text>
            {entries.map((e: any) => (
                <Flex key={e.src} align="center" justify="space-between" gap={4} mb={0.5}>
                    <Flex align="center" gap={1.5}>
                        <Box w="8px" h="8px" borderRadius="full" bg={e.color} flexShrink={0} />
                        <Text fontSize="xs" color="gray.600">{e.label}</Text>
                    </Flex>
                    <Flex align="center" gap={1}>
                        {hasPrior && (
                            <>
                                <Text fontSize="xs" color="gray.400">
                                    {e.priorValue === undefined ? '—' : formatter(e.priorValue)}
                                </Text>
                                <Text fontSize="xs" color="gray.300">&rarr;</Text>
                            </>
                        )}
                        <Text fontSize="xs" fontWeight="medium" color="gray.800">{formatter(e.value)}</Text>
                    </Flex>
                </Flex>
            ))}
            {!isPercent && (
                <Flex align="center" justify="space-between" gap={4} mt={1.5} pt={1.5} borderTop="1px solid" borderColor="gray.100">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.700">{totalLabel}</Text>
                    <Flex align="center" gap={1.5}>
                        <Text fontSize="xs" fontWeight="bold" color="#0071E3">{formatter(total)}</Text>
                        {totalChange !== undefined && isFinite(totalChange) && (
                            <Text fontSize="xs" fontWeight="medium" color={totalChange >= 0 ? 'green.500' : 'red.500'}>
                                {totalChange >= 0 ? '+' : ''}{totalChange.toFixed(1)}{changeLabel}
                                <Text as="span" color="gray.400" ml={0.5} fontWeight="normal">{comparisonLabel}</Text>
                            </Text>
                        )}
                        {hasPrior && totalChange === undefined && (
                            <Text fontSize="xs" color="gray.400">—</Text>
                        )}
                    </Flex>
                </Flex>
            )}
        </Box>
    );
};

const VerticalRankingChart = ({ 
    data, 
    isLoading, 
    metric, 
    domainLabel,
    maxItems = 12,
    height = 500,
    comparison,
    preset,
    showPriorBars = false,
    isVendor = false,
    isLocation = false,
    isInsurance = false,
    isOEM = false,
    marketplaceBreakdown = false,
}: VerticalRankingChartProps) => {
    const isFlexHeight = typeof height === 'string';
    const format = getMetricFormat(metric, isVendor, isLocation, isInsurance, isOEM);
    const metricLabel = getMetricLabel(metric, isVendor, isLocation, isInsurance, isOEM);
    const labelFormatter = (v: number) => formatChartValue(v, format);

    if (isLoading) {
        return (
            <Box h={isFlexHeight ? '100%' : undefined} flex={isFlexHeight ? 1 : undefined} minH={isFlexHeight ? 0 : undefined}>
                <Skeleton height={isFlexHeight ? '100%' : height} borderRadius="lg" />
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return (
            <Box
                h={isFlexHeight ? '100%' : height}
                flex={isFlexHeight ? 1 : undefined}
                minH={isFlexHeight ? 0 : undefined}
                display="flex"
                alignItems="center"
                justifyContent="center"
            >
                <Text color="gray.500">No data available</Text>
            </Box>
        );
    }

    // Pad is reserved first; names truncate to the leftover letter budget.
    const isFullscreenAxis = typeof height === 'string';
    const rankingValueFont = chartValueFontSize(isFullscreenAxis);
    const rankingYAxisFont = yAxisTickFontSize(isFullscreenAxis, 10);
    const axisWidth = yAxisWidth(isFullscreenAxis);
    const rankingBandMax = isFullscreenAxis ? undefined : DOMAIN_RANKING_AXIS_BAND_MAX;

    const chartData = data
        .filter(item => item && item.name)
        .slice(0, maxItems)
        .map(item => ({
            ...item,
            displayName: truncateRankingAxisName(item.name, isFullscreenAxis, rankingBandMax),
            fullName: item.name,
            priorMetricValue:
                typeof (item as any).prior?.[metric] === 'number'
                    ? Number((item as any).prior[metric])
                    : undefined,
        }));

    // ---- Marketplace breakdown ----------------------------------------------
    const useMarketplace =
        marketplaceBreakdown && chartData.some((d: any) => d.bySource && Object.keys(d.bySource).length > 0);

    const sourceTotals = new Map<string, number>();
    if (useMarketplace) {
        for (const d of chartData) {
            const bs = (d as any).bySource as Record<string, any> | undefined;
            if (!bs) continue;
            for (const [src, m] of Object.entries(bs)) {
                // Include prior so prior-only marketplaces (current 0) still rank/show.
                const v = (Number((m as any)?.[metric]) || 0)
                    + (Number((m as any)?.prior?.[metric]) || 0);
                sourceTotals.set(src, (sourceTotals.get(src) || 0) + Math.abs(v));
            }
        }
    }
    const sourceKeys = Array.from(sourceTotals.entries()).sort((a, b) => b[1] - a[1]).map(([s]) => s);

    // Does any source carry prior data? (backend attaches prior/change per source)
    const marketplaceHasPrior = useMarketplace && showPriorBars && chartData.some((d: any) => {
        const bs = d.bySource as Record<string, any> | undefined;
        if (!bs) return false;
        return sourceKeys.some((src) => typeof bs[src]?.prior?.[metric] === 'number');
    });

    const renderData: any[] = useMarketplace
        ? chartData.map((d: any) => {
            const row: any = { ...d };
            const bs = d.bySource as Record<string, any> | undefined;
            for (const src of sourceKeys) {
                row[`${SOURCE_SERIES_PREFIX}${src}`] = Number(bs?.[src]?.[metric]) || 0;
                if (marketplaceHasPrior) {
                    const priorSourceValue = bs?.[src]?.prior?.[metric];
                    row[`${SOURCE_SERIES_PREFIX}${src}__prior`] =
                        typeof priorSourceValue === 'number' ? priorSourceValue : undefined;
                }
            }
            return row;
        })
        : chartData;

    const hasComparisonBarData =
        !useMarketplace &&
        showPriorBars && chartData.some(d => typeof d.priorMetricValue === 'number' && !isNaN(d.priorMetricValue));

    const values = useMarketplace
        ? renderData
            .flatMap((d: any) => sourceKeys.flatMap((src) => [
                Number(d[`${SOURCE_SERIES_PREFIX}${src}`]) || 0,
                ...(marketplaceHasPrior && typeof d[`${SOURCE_SERIES_PREFIX}${src}__prior`] === 'number'
                    ? [d[`${SOURCE_SERIES_PREFIX}${src}__prior`]]
                    : []),
            ]))
            .filter((v) => !isNaN(v))
        : chartData
            .flatMap(d => [
                (d as any)[metric] || 0,
                ...(hasComparisonBarData && typeof d.priorMetricValue === 'number' ? [d.priorMetricValue] : []),
            ])
            .filter(v => !isNaN(v));
    const dataMin = values.length ? Math.min(...values) : 0;
    const dataMax = values.length ? Math.max(...values) : 0;

    const isPercent = format === 'percent';
    const rankingAxis = getRankingXAxisLayout(
        chartData.map((d: any) => d.displayName || ''),
        isFullscreenAxis,
        rankingBandMax,
    );
    const axisBand = rankingAxis.height;
    const axisMarginBottom = rankingAxis.bottom;
    const axisFontSize = rankingAxis.fontSize;
    // Plot pixel height (height minus top/bottom margins and the x-axis band) for the
    // guaranteed pixel gap below negative bars; estimate from viewport when flex (fullscreen).
    const plotHeightPx = typeof height === 'string'
        ? Math.max(300, (typeof window !== 'undefined' ? window.innerHeight : 800) - 266)
        : Math.max(0, height - MARKETPLACE_CHART_TOP_MARGIN - axisMarginBottom - axisBand);
    const [yMin, yMax] = getBarChartYDomain(dataMin, dataMax, {
        format,
        capPercentMaxAt100: false,
    }, plotHeightPx);

    const marketplaceLabelProps = (dataKey: string, isPrior: boolean) => ({
        dataKey,
        ...(isPrior || marketplaceHasPrior
            ? { content: comparisonBarLabelListContent(labelFormatter, rankingValueFont, true) }
            : verticalBarTopLabelListProps(labelFormatter, rankingValueFont, true)),
    });

    const chartTopMargin = MARKETPLACE_CHART_TOP_MARGIN;

    return (
        <Box
            position="relative"
            overflow="visible"
            h={isFlexHeight ? '100%' : undefined}
            flex={isFlexHeight ? 1 : undefined}
            minH={isFlexHeight ? 0 : undefined}
            display={isFlexHeight ? 'flex' : undefined}
            flexDirection={isFlexHeight ? 'column' : undefined}
        >
            {useMarketplace && <MarketplaceChartLegend sourceKeys={sourceKeys} isFullscreen={isFullscreenAxis} />}
            <Box flex={isFlexHeight ? 1 : undefined} minH={isFlexHeight ? 0 : undefined} w="100%" h={isFlexHeight ? '100%' : undefined}>
            <ResponsiveContainer width="100%" height={isFlexHeight ? '100%' : height}>
                <BarChart
                    data={renderData}
                    margin={{ top: chartTopMargin, right: 25, left: 0, bottom: axisMarginBottom }}
                    barGap={useMarketplace ? 2 : 4}
                    barCategoryGap={hasComparisonBarData ? '20%' : '30%'}
                >
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                    <XAxis
                        dataKey="displayName"
                        tick={{ fontSize: axisFontSize, fill: '#4A5568' }}
                        tickLine={false}
                        axisLine={{ stroke: '#E2E8F0' }}
                        angle={-45}
                        textAnchor="end"
                        interval={0}
                        height={axisBand}
                        tickMargin={RANKING_AXIS_TICK_MARGIN}
                    />
                    <YAxis
                        domain={[yMin, yMax]}
                        ticks={getBarChartYTicks(yMin, yMax)}
                        tickFormatter={(v) => (v === 0 && yMin >= 0 ? '' : formatChartValue(v, format))}
                        tick={{ fontSize: rankingYAxisFont, fill: '#718096' }}
                        tickLine={false}
                        axisLine={false}
                        width={axisWidth}
                        interval={0}
                        allowDecimals={isPercent}
                    />
                    {useMarketplace ? (
                        <Tooltip
                            content={<MarketplaceTooltip metric={metric} metricLabel={metricLabel} format={format} sourceKeys={sourceKeys} hasPrior={marketplaceHasPrior} preset={preset} />}
                            cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                        />
                    ) : (
                        <Tooltip
                            content={<CustomTooltip metric={metric} metricLabel={metricLabel} format={format} preset={preset} hasComparisonBars={hasComparisonBarData} />}
                            cursor={{ fill: 'rgba(0, 113, 227, 0.05)' }}
                        />
                    )}
                    {yMin < 0 && <ReferenceLine y={0} stroke="#CBD5E0" strokeDasharray="3 3" />}
                    {useMarketplace ? (
                        <>
                        {/* All prior bars first (faded), then all current bars, so each
                            period's marketplaces are grouped together. */}
                        {marketplaceHasPrior && sourceKeys.map((src, i) => (
                            <Bar
                                key={`${src}__prior`}
                                dataKey={`${SOURCE_SERIES_PREFIX}${src}__prior`}
                                name={`${getSourceLabel(src)} (prior)`}
                                legendType="none"
                                fill={getSourceColor(src, i)}
                                fillOpacity={0.4}
                                radius={[4, 4, 0, 0]}
                            >
                                <LabelList {...marketplaceLabelProps(`${SOURCE_SERIES_PREFIX}${src}__prior`, true)} />
                            </Bar>
                        ))}
                        {sourceKeys.map((src, i) => (
                            <Bar
                                key={src}
                                dataKey={`${SOURCE_SERIES_PREFIX}${src}`}
                                name={getSourceLabel(src)}
                                fill={getSourceColor(src, i)}
                                radius={[4, 4, 0, 0]}
                            >
                                <LabelList {...marketplaceLabelProps(`${SOURCE_SERIES_PREFIX}${src}`, false)} />
                            </Bar>
                        ))}
                        </>
                    ) : (
                        <>
                        {hasComparisonBarData && (
                            <Bar dataKey="priorMetricValue" radius={[4, 4, 0, 0]} fill="#CBD5E0" fillOpacity={0.9}>
                                {chartData.map((_, index) => (
                                    <Cell key={`prior-${index}`} fill="#CBD5E0" />
                                ))}
                                <LabelList
                                    dataKey="priorMetricValue"
                                    content={comparisonBarLabelListContent(labelFormatter, rankingValueFont)}
                                />
                            </Bar>
                        )}
                        <Bar dataKey={metric} radius={[4, 4, 0, 0]}>
                            {chartData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={PRIMARY_CHART_COLOR} />
                            ))}
                            <LabelList
                                dataKey={metric}
                                {...(hasComparisonBarData
                                    ? { content: comparisonBarLabelListContent(labelFormatter, rankingValueFont) }
                                    : verticalBarTopLabelListProps(labelFormatter, rankingValueFont))}
                            />
                        </Bar>
                        </>
                    )}
                </BarChart>
            </ResponsiveContainer>
            </Box>
        </Box>
    );
};

export default VerticalRankingChart;
